import './LabelingRules.scss';
import { ungzip } from 'pako';
import * as R from 'ramda';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdAdd, MdCheck, MdDelete, MdOutlineFileDownload, MdSmsFailed } from 'react-icons/md';
import {
  isBillingSegmentName,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { base64ToUint8Array } from '@blisspointmedia/bpm-types/dist/binaryUtils';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Intent, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { IconButton } from '@Components/IconButton';
import { PendingChangesControls, PendingChangesPanel } from '@Components/PendingChanges';
import { RowType } from '@Components/PendingChanges/pending-changes-utils';
import { FullPageSpinner } from '@Components/Spinner';
import { Header } from '@Components/StickyTable';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';
import { Success500, Warning500 } from '../../utils/colors';
import { download } from '../../utils/download-utils';
import { awaitText, SegmentationMappingLambdaFetch } from '../../utils/fetch-utils';
import { useStateFunction } from '../../utils/hooks/useData';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { LabelingRulesModal } from './LabelingRulesModal';
import { LabelingRulesTable } from './LabelingRulesTable';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';
import {
  buildIfStatement,
  formatDateRange,
  getSegmentName,
  getSegmentValue,
  isBillingRule
} from './segmentationMappingUtils';

export type { BillingMappingLabelRule } from './segmentation-mapping-constants';

type AnyLabelRule = SegmentationMappingLabelRule | BillingMappingLabelRule;

const omitMetaFields = (rule: AnyLabelRule): Partial<AnyLabelRule> =>
  R.omit(['updatedAt', 'lastUpdatedBy', 'segmentName', 'segmentValueName', 'ifStatement'], rule);

const isLabelRule = (value: unknown): value is AnyLabelRule =>
  typeof value === 'object' && value !== null && 'rulePriority' in value;

const formatFilterTokens = (row: RowType): string => {
  const tokens = row.filterTokens;
  if (Array.isArray(tokens) && tokens.length > 0) {
    const labels = tokens.map(token =>
      token && typeof token === 'object' && 'label' in token ? String(token.label) : String(token)
    );
    return row.isAdvanced ? labels.join(' ') : `* is like ${labels.join(' or ')}`;
  }
  return 'No filters set';
};

const toRuleRow = (rule: AnyLabelRule): RowType => {
  const row: RowType = { ...rule };
  row.isBilling = isBillingRule(rule);
  row.dateRange = isBillingRule(rule) ? formatDateRange(rule.startDate, rule.endDate) : undefined;
  row.ifStatement = formatFilterTokens(row);
  row._raw = rule;
  return row;
};

const buildRuleHeaders =
  (customSegments: CustomSegmentsData[]) =>
  (row: RowType): Header[] => {
    const nameCol: Header = {
      field: 'name',
      label: 'Name',
      renderer: (r: RowType) => String(r.name ?? '') || 'Not Set'
    };
    const ifCol: Header = { field: 'ifStatement', label: 'If Statement' };
    const typeCol: Header = {
      field: 'isAdvanced',
      label: 'Rule Type',
      renderer: (r: RowType) => (r.isAdvanced ? 'Advanced' : 'Basic')
    };
    const enabledCol: Header = {
      field: 'isActive',
      label: 'Enabled',
      renderer: (r: RowType) => (r.isActive ? 'Yes' : 'No')
    };
    const priorityCol: Header = { field: 'rulePriority', label: 'Rule Priority' };

    if (row.isBilling) {
      return [
        nameCol,
        {
          field: 'setBillingSegment',
          label: 'Billing Segment',
          renderer: (r: RowType) => String(r.setBillingSegment ?? '') || 'Not Set'
        },
        {
          field: 'setBillingSegmentValue',
          label: 'Billing Segment Value',
          renderer: (r: RowType) => String(r.setBillingSegmentValue ?? '') || 'Not Set'
        },
        ifCol,
        typeCol,
        enabledCol,
        priorityCol,
        { field: 'dateRange', label: 'Date Range' }
      ];
    }
    return [
      nameCol,
      {
        field: 'setSegmentId',
        label: 'Segment',
        renderer: (r: RowType) =>
          getSegmentName(Number(r.setSegmentId), customSegments) || 'Not Set'
      },
      {
        field: 'setSegmentValueId',
        label: 'Segment Value',
        renderer: (r: RowType) =>
          getSegmentValue(Number(r.setSegmentValueId), customSegments) || 'Not Set'
      },
      ifCol,
      typeCol,
      enabledCol,
      priorityCol
    ];
  };

const buildRuleTitle = (row: RowType, customSegments: CustomSegmentsData[]): string => {
  const raw = row._raw;
  if (!isLabelRule(raw)) return '';
  const billing = isBillingRule(raw);
  const segmentName = billing
    ? raw.setBillingSegment
    : getSegmentName(raw.setSegmentId, customSegments);
  if (raw.name) return `${raw.company} - ${segmentName} - ${raw.name}`;
  const segmentValueName = billing
    ? raw.setBillingSegmentValue
    : getSegmentValue(raw.setSegmentValueId, customSegments);
  return `${raw.company} - ${segmentName} to ${segmentValueName} - NEW`;
};

const ALERT_TYPES = {
  NAV_AWAY: 'nav',
  DISCARD: 'discard',
  SUCCESS: 'success',
  ERROR: 'error'
} as const;
type AlertTypeKey = (typeof ALERT_TYPES)[keyof typeof ALERT_TYPES];

interface LabelingRulesProps {
  data: unknown[] | null;
  rules: SegmentationMappingLabelRule[] | undefined;
  billingRules: BillingMappingLabelRule[] | undefined;
  customSegments: CustomSegmentsData[];
  dataGranularity: 'ad' | 'ad_group' | 'campaign';
  onSave: (rules: SegmentationMappingLabelRule[], billingRules: BillingMappingLabelRule[]) => void;
  setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>;
}

export const LabelingRules = ({
  data,
  rules,
  billingRules,
  customSegments,
  dataGranularity = 'campaign',
  onSave,
  setHasUnsavedChanges = () => {}
}: LabelingRulesProps) => {
  const user = useAppSelector(UserRedux.fullNameSelector);
  const { cid } = useCompanyInfo();
  const [error, setError] = useState<Error>();

  const [activeTab, setActiveTab] = useState<string>('all');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [originalRules, setOriginalRules] = useState<SegmentationMappingLabelRule[]>(rules ?? []);
  const [rulesList, setRulesList] = useState<SegmentationMappingLabelRule[]>(rules ?? []);
  const [originalBillingRules, setOriginalBillingRules] = useState<BillingMappingLabelRule[]>(
    billingRules ?? []
  );
  const [billingRulesList, setBillingRulesList] = useState<BillingMappingLabelRule[]>(
    billingRules ?? []
  );

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);

  const isReorderable = activeTab !== 'all';

  const newRules = useMemo(
    () => rulesList?.filter(r => !r.id)?.sort((a, b) => a.setSegmentId - b.setSegmentId) || [],
    [rulesList]
  );

  const deletedRules = useMemo(
    () =>
      originalRules
        .filter(orig => !rulesList?.find(r => r.id === orig.id))
        .sort((a, b) => a.setSegmentId - b.setSegmentId) || [],
    [originalRules, rulesList]
  );

  const editedRules = useMemo(
    () =>
      rulesList
        ?.filter(rule => {
          if (!rule.id) {
            return false;
          }
          const original = originalRules.find(o => o.id === rule.id);
          if (!original) {
            return false;
          }
          return !R.equals(omitMetaFields(rule), omitMetaFields(original));
        })
        .sort((a, b) => a.setSegmentId - b.setSegmentId) || [],
    [rulesList, originalRules]
  );

  const newBillingRules = useMemo(
    () =>
      billingRulesList
        ?.filter(r => !r.id)
        ?.sort((a, b) => a.setBillingSegment.localeCompare(b.setBillingSegment)) || [],
    [billingRulesList]
  );

  const deletedBillingRules = useMemo(
    () =>
      originalBillingRules
        .filter(orig => !billingRulesList?.find(r => r.id === orig.id))
        .sort((a, b) => a.setBillingSegment.localeCompare(b.setBillingSegment)) || [],
    [originalBillingRules, billingRulesList]
  );

  const editedBillingRules = useMemo(
    () =>
      billingRulesList
        ?.filter(rule => {
          if (!rule.id) {
            return false;
          }
          const original = originalBillingRules.find(o => o.id === rule.id);
          if (!original) {
            return false;
          }
          return !R.equals(omitMetaFields(rule), omitMetaFields(original));
        })
        .sort((a, b) => a.setBillingSegment.localeCompare(b.setBillingSegment)) || [],
    [billingRulesList, originalBillingRules]
  );

  const getFreshRuleData = useCallback(async () => {
    let ruleData: SegmentationMappingLabelRule[] | null = null;
    let billingRuleData: BillingMappingLabelRule[] | null = null;

    try {
      const res = await SegmentationMappingLambdaFetch('/getSegmentLabelingRules', {
        params: {
          company: cid
        }
      });
      const resTextZippedBase64 = await awaitText(res);
      if (resTextZippedBase64.indexOf('"') === 0) {
        const resText = ungzip(base64ToUint8Array(resTextZippedBase64.replaceAll('"', '')), {
          toText: true
        });
        ruleData = JSON.parse(resText) as SegmentationMappingLabelRule[];
      } else {
        ruleData = JSON.parse(resTextZippedBase64) as SegmentationMappingLabelRule[];
      }
      setOriginalRules(ruleData);
      setRulesList(ruleData);
    } catch (e) {
      setError(e);
    }
    try {
      const resBilling = await SegmentationMappingLambdaFetch('/getBillingLabelingRules', {
        params: {
          company: cid
        }
      });
      const resBillingTextZippedBase64 = await awaitText(resBilling);
      if (resBillingTextZippedBase64.indexOf('"') === 0) {
        const resText = ungzip(base64ToUint8Array(resBillingTextZippedBase64.replaceAll('"', '')), {
          toText: true
        });
        billingRuleData = JSON.parse(resText) as BillingMappingLabelRule[];
      } else {
        billingRuleData = JSON.parse(resBillingTextZippedBase64) as BillingMappingLabelRule[];
      }
      setOriginalBillingRules(billingRuleData);
      setBillingRulesList(billingRuleData);
    } catch (e) {
      setError(e);
    }
    onSave(ruleData ?? rules ?? [], billingRuleData ?? billingRules ?? []);
  }, [cid, onSave, rules, billingRules]);

  const simpleCustomSegments = useMemo(
    () =>
      customSegments?.filter(segment => {
        const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
        return !systemNames.includes(segment.segmentName);
      }),
    [customSegments]
  );

  const pageNavs = useMemo(() => {
    const tabs = [{ label: 'All Segments', key: 'all' }];
    const customTabs = simpleCustomSegments?.map(segment => ({
      label: segment.segmentName,
      key: segment.segmentName
    }));
    return customTabs ? tabs.concat(customTabs) : tabs;
  }, [simpleCustomSegments]);

  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screenWidth]);

  const visibleTabs = useMemo(() => {
    const avgCharLength = 13;
    const padding = 24;

    let tabWidthSum = 0;
    let maxVisibleTabs = 0;
    const maxOverflowLabelLength =
      avgCharLength * Math.max(...pageNavs.map(item => item.label.length));

    while (maxVisibleTabs < pageNavs.length && tabWidthSum < screenWidth - maxOverflowLabelLength) {
      // Padding between each tab is 24px, estimating that each character takes up 13px
      tabWidthSum += padding + avgCharLength * pageNavs[maxVisibleTabs].label.length;
      maxVisibleTabs++;
    }
    if (maxVisibleTabs === pageNavs.length) {
      // if there's no overflow, we don't need room for the "More..." button, so we can display one more tab
      maxVisibleTabs += 1;
    }
    return pageNavs.slice(0, maxVisibleTabs);
  }, [pageNavs, screenWidth]);

  const overflowTabs = useMemo(() => pageNavs.slice(visibleTabs.length), [pageNavs, visibleTabs]);

  const segmentNameById = useMemo(
    () =>
      new Map(
        (customSegments ?? []).map((s): [number, string] => [Number(s.segmentId), s.segmentName])
      ),
    [customSegments]
  );

  const valueNameById = useMemo(
    () =>
      new Map(
        (simpleCustomSegments ?? [])
          .flatMap(s => s.values)
          .map((v): [number, string] => [Number(v.valueId), v.valueName])
      ),
    [simpleCustomSegments]
  );

  const findSegmentName = useCallback(
    (segmentId: number): string => segmentNameById.get(segmentId) ?? '',
    [segmentNameById]
  );

  const findSegmentValue = useCallback(
    (valueId: number): string => valueNameById.get(valueId) ?? '',
    [valueNameById]
  );

  const dataToUse = useMemo(() => {
    const modifiedData = rulesList?.map(rule => {
      const segmentName = findSegmentName(rule.setSegmentId);
      const segmentValueName = findSegmentValue(rule.setSegmentValueId);
      const { filterTokens } = rule;
      const ifStatement = buildIfStatement(filterTokens, rule.isAdvanced);
      return { ...rule, segmentName, segmentValueName, ifStatement };
    });
    const modifiedBillingData = billingRulesList?.map(rule => {
      const segmentName = rule.setBillingSegment;
      const segmentValueName = rule.setBillingSegmentValue;
      const { filterTokens } = rule;
      const ifStatement = buildIfStatement(filterTokens, rule.isAdvanced);
      return { ...rule, segmentName, segmentValueName, ifStatement };
    });
    modifiedData?.sort((a, b) => {
      if (a.setSegmentId !== b.setSegmentId) {
        return a.setSegmentId - b.setSegmentId;
      }
      return a.rulePriority - b.rulePriority;
    });
    modifiedBillingData?.sort((a, b) => {
      const nameA = a.segmentName || '';
      const nameB = b.segmentName || '';
      const segmentNameCompare = nameA.localeCompare(nameB);
      if (segmentNameCompare !== 0) {
        return segmentNameCompare;
      }

      const valueA = a.segmentValueName || '';
      const valueB = b.segmentValueName || '';
      return valueA.localeCompare(valueB);
    });
    if (activeTab === 'all') {
      return {
        data: [...modifiedData, ...modifiedBillingData]
      };
    }

    if (isBillingSegmentName(activeTab)) {
      const relevantBillingRules =
        modifiedBillingData?.filter(rule => rule.segmentName === activeTab) || [];
      return { data: relevantBillingRules.sort((a, b) => a.rulePriority - b.rulePriority) };
    }
    const relevantRules = modifiedData?.filter(rule => rule.segmentName === activeTab) || [];
    return { data: relevantRules.sort((a, b) => a.rulePriority - b.rulePriority) };
  }, [activeTab, findSegmentName, findSegmentValue, rulesList, billingRulesList]);

  const [filter, setFilter] = useStateFunction<(line: object) => boolean>(() => true);

  const filteredData = useMemo(() => dataToUse.data?.filter(filter), [dataToUse.data, filter]);

  const filterBarOptions = useMemo(
    () => [
      { name: 'name', label: 'Rule ID' },
      { name: 'segmentName', label: 'Segment' },
      { name: 'segmentValueName', label: 'Segment Value' },
      { name: 'ifStatement', label: 'If statement' },
      { name: 'createdBy', label: 'Created By' },
      { name: 'lastUpdatedBy', label: 'Last Updated By' }
    ],
    []
  );

  const newPriority = useCallback(
    (newRule: AnyLabelRule) => {
      if (!dataToUse.data?.length) {
        return 1;
      }
      if ('setSegmentId' in newRule) {
        const relevantRules = rulesList.filter(r => r?.setSegmentId === newRule?.setSegmentId);
        return relevantRules.length ? Math.max(...relevantRules.map(r => r.rulePriority)) + 1 : 1;
      }
      const relevantRules = billingRulesList.filter(
        r => r?.setBillingSegment === newRule?.setBillingSegment
      );
      return relevantRules.length ? Math.max(...relevantRules.map(r => r.rulePriority)) + 1 : 1;
    },
    [dataToUse.data?.length, rulesList, billingRulesList]
  );

  const [ruleToEdit, setRuleToEdit] = useState<AnyLabelRule | null>(null);
  const [ruleToCopy, setRuleToCopy] = useState<AnyLabelRule | null>(null);

  const resetModalState = useCallback(() => {
    setRuleToEdit(null);
    setRuleToCopy(null);
  }, []);

  useEffect(() => {
    if (ruleToCopy) {
      setShowModal(true);
    }
  }, [ruleToCopy]);

  const handleRuleAdd = useCallback(
    (rule: AnyLabelRule) => {
      const date = new Date().toISOString();
      if ('setSegmentId' in rule && 'setSegmentValueId' in rule) {
        const newRule = {
          ...rule,
          isActive: true,
          createdBy: user ?? '',
          lastUpdatedBy: user ?? '',
          createdAt: date,
          updatedAt: date,
          rulePriority: newPriority(rule),
          company: cid,
          segmentName: getSegmentName(rule.setSegmentId, simpleCustomSegments),
          segmentValueName: getSegmentValue(rule.setSegmentValueId, simpleCustomSegments)
        };
        setRulesList([...(rulesList || []), newRule]);
      } else {
        const newBillingRule = {
          ...rule,
          isActive: true,
          createdBy: user ?? '',
          lastUpdatedBy: user ?? '',
          createdAt: date,
          updatedAt: date,
          rulePriority: newPriority(rule),
          company: cid
        };
        setBillingRulesList([...(billingRulesList || []), newBillingRule]);
      }
      setRuleToCopy(null);
      setShowModal(false);
    },
    [cid, newPriority, rulesList, billingRulesList, simpleCustomSegments, user]
  );

  const handleRuleEdit = useCallback(
    (editedRule: AnyLabelRule) => {
      const newlyEditedRule = {
        ...editedRule
      };

      let updatedRules;
      let originalRule;
      let rulesListToUse;

      if (editedRule.id) {
        originalRule = originalRules?.find(rule => rule.id === editedRule.id);
        rulesListToUse = rulesList;
        if (!originalRule) {
          // check if billing rule
          originalRule = originalBillingRules?.find(rule => rule.id === editedRule.id);
          rulesListToUse = billingRulesList;
          if (!originalRule) {
            return;
          }
        }
        // Remove metadata fields from both rules before comparing
        const cleanedNew = omitMetaFields(newlyEditedRule);
        const cleanedOriginal = omitMetaFields(originalRule);
        const isSubstantivelyDifferent = !R.equals(cleanedNew, cleanedOriginal);

        if (isSubstantivelyDifferent) {
          updatedRules = rulesListToUse?.map(rule =>
            rule.id === newlyEditedRule.id ? { ...rule, ...newlyEditedRule } : rule
          );
        } else {
          updatedRules = rulesListToUse?.map(rule =>
            rule.id === newlyEditedRule.id ? originalRule : rule
          );
        }
      } else {
        rulesListToUse = isBillingRule(editedRule) ? billingRulesList : rulesList;
        updatedRules = rulesListToUse?.map(rule =>
          rule.createdAt === editedRule.createdAt ? { ...rule, ...newlyEditedRule } : rule
        );
      }
      if (rulesListToUse === rulesList) {
        setRulesList(
          updatedRules?.map(rule => {
            const segmentName = getSegmentName(rule.setSegmentId, simpleCustomSegments);
            const segmentValueName = getSegmentValue(rule.setSegmentValueId, simpleCustomSegments);
            return { ...rule, segmentName, segmentValueName };
          })
        );
      } else {
        setBillingRulesList(updatedRules);
      }
      setRuleToEdit(null);
      setShowModal(false);
    },
    [
      originalRules,
      originalBillingRules,
      rulesList,
      billingRulesList,
      simpleCustomSegments,
      setRulesList,
      setBillingRulesList
    ]
  );

  const handleRuleReorder = useCallback(
    (reorderedRules: SegmentationMappingLabelRule[] | BillingMappingLabelRule[]) => {
      const reorderedWithUpdatedPriority = reorderedRules.map((rule, index) => {
        const newPriorityValue = index + 1;
        return rule.rulePriority !== newPriorityValue
          ? {
              ...rule,
              rulePriority: newPriorityValue,
              updatedAt: new Date().toISOString(),
              lastUpdatedBy: user
            }
          : rule;
      });
      if ('setSegmentId' in reorderedRules[0]) {
        setRulesList(prevRules =>
          prevRules.map(rule => {
            const updated = reorderedWithUpdatedPriority.find(
              r => (r.id && r.id === rule.id) || (!r.id && r.createdAt === rule.createdAt)
            );
            return updated ? { ...rule, rulePriority: updated.rulePriority } : rule;
          })
        );
      } else {
        setBillingRulesList(prevRules =>
          prevRules.map(rule => {
            const updated = reorderedWithUpdatedPriority.find(
              r => (r.id && r.id === rule.id) || (!r.id && r.createdAt === rule.createdAt)
            );
            return updated ? { ...rule, rulePriority: updated.rulePriority } : rule;
          })
        );
      }
    },
    [user]
  );

  const handleRuleDelete = useCallback(
    (rule: AnyLabelRule) => {
      if ('setSegmentId' in rule) {
        const newRuleList = rule.id
          ? rulesList?.filter(r => r.id !== rule.id)
          : rulesList?.filter(r => r.createdAt !== rule.createdAt);
        setRulesList(newRuleList || []);
      } else {
        const newBillingRuleList = rule.id
          ? billingRulesList?.filter(r => r.id !== rule.id)
          : billingRulesList?.filter(r => r.createdAt !== rule.createdAt);
        setBillingRulesList(newBillingRuleList || []);
      }
    },
    [rulesList, billingRulesList, setRulesList, setBillingRulesList]
  );

  const exportToCSV = useCallback(() => {
    const isBillingTab = isBillingSegmentName(activeTab);

    // If statement / Created / Modified / Enabled — identical for both rule shapes.
    const buildCommonTail = (rule: AnyLabelRule): string[] => {
      const statement =
        rule.isAdvanced == null ? '' : buildIfStatement(rule.filterTokens, rule.isAdvanced);
      const createdDate = new Date(rule.updatedAt || rule.createdAt || '').toLocaleDateString();
      const modifiedDate = new Date(rule.updatedAt || '').toLocaleDateString();
      return [
        statement,
        `${rule.createdBy || ''} ${createdDate}`,
        `${rule.lastUpdatedBy || ''} ${modifiedDate}`,
        rule.isActive ? 'Yes' : 'No'
      ];
    };

    const headers: string[] = [];
    if (isReorderable) {
      headers.push('Rule Priority');
    }
    headers.push('Rule ID');
    headers.push(
      ...(isBillingTab
        ? ['Billing Segment', 'Billing Segment Value', 'Date Range']
        : ['Segment', 'Segment Value'])
    );
    headers.push('If statement', 'Created', 'Modified', 'Enabled');

    const csvRows: string[][] = [headers];

    if (isBillingTab) {
      const rulesToExport = originalBillingRules.filter(
        rule => rule.setBillingSegment === activeTab
      );
      if (rulesToExport.length === 0) {
        return;
      }
      rulesToExport.forEach(rule => {
        csvRows.push([
          ...(isReorderable ? [String(rule.rulePriority || '')] : []),
          rule.name || '-',
          rule.setBillingSegment || '-',
          rule.setBillingSegmentValue || '-',
          formatDateRange(rule.startDate, rule.endDate),
          ...buildCommonTail(rule)
        ]);
      });
    } else {
      const rulesToExport =
        activeTab === 'all'
          ? originalRules
          : originalRules.filter(rule => findSegmentName(rule.setSegmentId) === activeTab);
      if (rulesToExport.length === 0) {
        return;
      }
      rulesToExport.forEach(rule => {
        csvRows.push([
          ...(isReorderable ? [String(rule.rulePriority || '')] : []),
          rule.name || '-',
          findSegmentName(rule.setSegmentId),
          findSegmentValue(rule.setSegmentValueId),
          ...buildCommonTail(rule)
        ]);
      });
    }

    const csvContent = csvRows
      .map(row =>
        row
          .map(cell => {
            const cellStr = String(cell || '');
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(',')
      )
      .join('\n');

    const tabName = activeTab === 'all' ? '' : activeTab.replace(/\s+/g, '_').toLowerCase();
    const fileName = `${cid}_labeling_rules${tabName ? `_${tabName}` : ''}_${
      new Date().toISOString().split('T')[0]
    }.csv`;

    download(csvContent, fileName, 'text/csv');
  }, [
    cid,
    originalRules,
    originalBillingRules,
    activeTab,
    isReorderable,
    findSegmentName,
    findSegmentValue
  ]);

  const hasPendingChanges = useMemo(
    () =>
      newRules.length > 0 ||
      editedRules.length > 0 ||
      deletedRules.length > 0 ||
      newBillingRules.length > 0 ||
      editedBillingRules.length > 0 ||
      deletedBillingRules.length > 0,
    [
      newRules.length,
      editedRules.length,
      deletedRules.length,
      newBillingRules.length,
      editedBillingRules.length,
      deletedBillingRules.length
    ]
  );

  useEffect(() => {
    setHasUnsavedChanges(hasPendingChanges);
  }, [hasPendingChanges, setHasUnsavedChanges]);

  // rules/billingRules become undefined while a refetch (e.g. after segment/value
  // deletion elsewhere) is in flight, then resolve to the fresh array - sync local
  // state to it unless the user has unsaved edits in progress.
  useEffect(() => {
    if (hasPendingChanges || rules === undefined) return;
    setOriginalRules(rules);
    setRulesList(rules);
  }, [rules, hasPendingChanges]);

  useEffect(() => {
    if (hasPendingChanges || billingRules === undefined) return;
    setOriginalBillingRules(billingRules);
    setBillingRulesList(billingRules);
  }, [billingRules, hasPendingChanges]);

  const handleClearPendingChanges = useCallback(() => {
    setRulesList(originalRules ? [...originalRules] : []);
    setBillingRulesList(originalBillingRules ? [...originalBillingRules] : []);
  }, [setRulesList, originalRules, setBillingRulesList, originalBillingRules]);

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<AlertTypeKey>(ALERT_TYPES.NAV_AWAY);
  const [navToTab, setNavToTab] = useState<string>('');

  const save = useCallback(async () => {
    try {
      setSaving(true);
      const segBody = {
        company: cid,
        newRules,
        updatedRules: editedRules,
        deletedRules: deletedRules.map(rule => rule.id)
      };
      const billingBody = {
        company: cid,
        newBillingRules,
        updatedBillingRules: editedBillingRules,
        deletedBillingRules: deletedBillingRules.map(rule => rule.id)
      };

      await Promise.all([
        SegmentationMappingLambdaFetch('/bulkUpdateSegmentMappingLabelRules', {
          method: 'POST',
          body: segBody
        }),
        SegmentationMappingLambdaFetch('/bulkUpdateBillingLabelingRules', {
          method: 'POST',
          body: billingBody
        })
      ]);
      await getFreshRuleData();
      setSaving(false);
      setShowPendingChanges(false);
      setAlertType(ALERT_TYPES.SUCCESS);
      setShowAlertModal(true);
    } catch (e) {
      setSaving(false);
      let ruleMessage = `Failed to update: ${e.message}`;
      if (
        e.message ===
        'duplicate key value violates unique constraint "segment_mapping_label_rules_company_name_idx".'
      ) {
        ruleMessage = `${ruleMessage} At least some of the rules you entered are already in the database`;
      }
      setError(new Error(ruleMessage));
      setAlertType(ALERT_TYPES.ERROR);
      setShowAlertModal(true);
    }
  }, [
    cid,
    newRules,
    editedRules,
    deletedRules,
    newBillingRules,
    editedBillingRules,
    deletedBillingRules,
    getFreshRuleData,
    setError
  ]);

  const alertModalParams = useMemo(
    () => ({
      nav: {
        title: 'Are you sure you want to leave?',
        body: 'If you leave now, your changes will not be saved.',
        icon: <MdSmsFailed color={Warning500} size={75} />,
        intent: Intent.ERROR,
        primaryAction: { onClick: () => setShowAlertModal(false), label: 'Stay' },
        secondaryAction: {
          onClick: () => {
            setActiveTab(navToTab);
            setRulesList(originalRules);
            setShowAlertModal(false);
          },
          label: 'Leave'
        }
      },
      discard: {
        title: 'Discard changes?',
        body: "Any edits you've made won't be saved and you will have to start over.",
        icon: <MdDelete color={Warning500} size={75} />,
        intent: Intent.WARNING,
        primaryAction: {
          onClick: () => {
            handleClearPendingChanges();
            setShowAlertModal(false);
          },
          label: 'Discard'
        },
        secondaryAction: { onClick: () => setShowAlertModal(false) }
      },
      success: {
        title: 'Segment Labeling Rules Saved!',
        body: `Go to the labeling page to label ${dataGranularity.replace(
          '_',
          ' '
        )}s with these rules.`,
        icon: <MdCheck color={Success500} size={75} />,
        intent: Intent.SUCCESS,
        primaryAction: {
          onClick: () => {
            handleClearPendingChanges();
            setShowAlertModal(false);
            window.location.assign(
              `${window.location.origin}/${cid}/segmentation-mapping/labeling`
            );
          },
          label: 'Go to labeling page'
        },
        secondaryAction: {
          onClick: () => {
            setShowAlertModal(false);
            handleClearPendingChanges();
          },
          label: 'Stay'
        }
      },
      error: {
        title: 'Error',
        body: (
          <div>
            <div>There was an error saving your changes:</div>
            <div className='labeling-rules-error-message'>{error?.message}</div>
          </div>
        ),
        icon: <MdSmsFailed color={Warning500} size={75} />,
        intent: Intent.ERROR,
        primaryAction: { onClick: () => setShowAlertModal(false) },
        secondaryAction: undefined
      }
    }),
    [cid, dataGranularity, error?.message, handleClearPendingChanges, navToTab, originalRules]
  );

  if (!data) {
    return <FullPageSpinner />;
  }

  return (
    <div className='labeling-rules-page'>
      <LabelingRulesModal
        rule={ruleToEdit || ruleToCopy || undefined}
        show={showModal}
        setShow={show => {
          if (!show) {
            resetModalState();
            setShowModal(false);
          } else {
            setShowModal(true);
          }
        }}
        onRuleSave={ruleToEdit ? handleRuleEdit : handleRuleAdd}
        customSegments={customSegments}
        dataGranularity={dataGranularity}
        isNew={!ruleToEdit || ruleToCopy != null}
        data={data}
        rules={rulesList || []}
        activeTab={activeTab}
      />
      <AlertDialog
        title={alertModalParams[alertType].title}
        body={alertModalParams[alertType].body}
        icon={alertModalParams[alertType].icon}
        intent={alertModalParams[alertType].intent}
        show={showAlertModal}
        primaryAction={alertModalParams[alertType].primaryAction}
        secondaryAction={alertModalParams[alertType].secondaryAction}
        onHide={() => setShowAlertModal(false)}
      />
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value ?? '';
          setNavToTab(valueTab);
          if (!valueTab || valueTab === activeTab) {
            return;
          }
          if (hasPendingChanges) {
            setShowAlertModal(true);
            setAlertType(ALERT_TYPES.NAV_AWAY);
          } else {
            setActiveTab(valueTab);
            setNavToTab('');
          }
        }}
      >
        <div className='nav-tabs-row'>
          {visibleTabs.map(nav => (
            <Nav.Item key={nav.key}>
              <Nav.Link eventKey={nav.key}>{nav.label}</Nav.Link>
            </Nav.Item>
          ))}
        </div>
        {overflowTabs.length > 0 && (
          <div className='nav-overflow'>
            <Nav.Item>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                className='nav-dropdown'
                menuAlign='end'
                options={overflowTabs.map(nav => ({
                  label: nav.label,
                  value: nav.key
                }))}
                onChange={value => {
                  setActiveTab(value);
                  setNavToTab('');
                }}
                placeholder='More...'
                value={overflowTabs.map(nav => nav.key).includes(activeTab) ? activeTab : ''}
              />
            </Nav.Item>
          </div>
        )}
      </Nav>
      <div className='labeling-rules-body'>
        <div className='filter-bar-container'>
          <FilterBar
            size={Size.LG}
            options={filterBarOptions}
            lines={dataToUse.data || []}
            onFilter={setFilter}
            hasAdvanced
          />
          <div className='rule-controls'>
            <Button
              size={Size.LG}
              leadingElement={<MdAdd />}
              onClick={() => {
                resetModalState();
                setShowModal(true);
              }}
            >
              {activeTab === 'all' ? 'New Rule' : `New ${activeTab} Rule`}
            </Button>
            <IconButton onClick={exportToCSV}>
              <MdOutlineFileDownload />
            </IconButton>
          </div>
        </div>
        <LabelingRulesTable
          rules={(filteredData || []) as unknown as SegmentationMappingLabelRule[]}
          activeTab={activeTab}
          customSegments={customSegments}
          onRuleDelete={handleRuleDelete}
          onLiveToggle={rule =>
            handleRuleEdit({
              ...rule,
              isActive: !rule.isActive
            })
          }
          setModalShow={show => {
            if (show) {
              resetModalState();
              setShowModal(true);
            }
          }}
          setRuleToEdit={rule => {
            resetModalState();
            setRuleToEdit(rule);
          }}
          setRuleToCopy={rule => {
            resetModalState();
            setRuleToCopy(rule);
          }}
          onRuleReorder={handleRuleReorder}
        />
        <div className='labeling-rule-changes-controls'>
          <PendingChangesControls
            hasPendingChanges={hasPendingChanges}
            setShowPendingChanges={setShowPendingChanges}
            clearAllChanges={() => {
              setAlertType(ALERT_TYPES.DISCARD);
              setShowAlertModal(true);
            }}
            saveChanges={save}
            isSaving={saving}
          />
        </div>
        {showPendingChanges && hasPendingChanges && (
          <PendingChangesPanel
            headers={buildRuleHeaders(customSegments)}
            originalData={Object.fromEntries(
              [...originalRules, ...originalBillingRules].map(rule => [
                String(rule.id),
                toRuleRow(rule)
              ])
            )}
            pendingChanges={{
              editedRows: [...editedRules, ...editedBillingRules].map(toRuleRow),
              newRows: [...newRules, ...newBillingRules].map(toRuleRow),
              deletedRows: [...deletedRules, ...deletedBillingRules].map(toRuleRow)
            }}
            pendingRowHeaderRender={row => buildRuleTitle(row, customSegments)}
            showPendingChanges={showPendingChanges}
            setShowPendingChanges={setShowPendingChanges}
          />
        )}
      </div>
    </div>
  );
};
